<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const emit = defineEmits(['filter']);

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const { INBOX_FILTER_TYPE, INBOX_DISPLAY_BY, INBOX_SORT_BY } = wootConstants;

const showSortMenu = ref(false);
const activeSort = ref(INBOX_SORT_BY.NEWEST);
const activeDisplayFilter = reactive({ status: '', type: '' });

const displayOptions = ref([
  {
    name: t('INBOX.DISPLAY_MENU.DISPLAY_OPTIONS.SNOOZED'),
    key: INBOX_DISPLAY_BY.SNOOZED,
    selected: false,
    type: INBOX_FILTER_TYPE.STATUS,
  },
  {
    name: t('INBOX.DISPLAY_MENU.DISPLAY_OPTIONS.READ'),
    key: INBOX_DISPLAY_BY.READ,
    selected: false,
    type: INBOX_FILTER_TYPE.TYPE,
  },
]);

const sortOptions = [
  {
    name: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.NEWEST'),
    key: INBOX_SORT_BY.NEWEST,
    type: INBOX_FILTER_TYPE.SORT_ORDER,
  },
  {
    name: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.OLDEST'),
    key: INBOX_SORT_BY.OLDEST,
    type: INBOX_FILTER_TYPE.SORT_ORDER,
  },
];

const activeSortOption = computed(
  () => sortOptions.find(option => option.key === activeSort.value)?.name || ''
);

const saveSelectedDisplayFilter = () => {
  updateUISettings({
    inbox_filter_by: {
      ...activeDisplayFilter,
      sort_by: activeSort.value || INBOX_SORT_BY.NEWEST,
    },
  });
};

const updateDisplayOption = option => {
  displayOptions.value.forEach(displayOption => {
    if (displayOption.key === option.key) {
      displayOption.selected = !option.selected;
      activeDisplayFilter[displayOption.type] = displayOption.selected
        ? displayOption.key
        : '';
      saveSelectedDisplayFilter();
      emit('filter', option);
    }
  });
};

const openSortMenu = () => {
  showSortMenu.value = !showSortMenu.value;
};

const onSortOptionClick = option => {
  activeSort.value = option.key;
  showSortMenu.value = false;
  saveSelectedDisplayFilter();
  emit('filter', option);
};

const setSavedFilter = () => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { status, type, sort_by: sortBy } = filterBy;
  activeSort.value = sortBy || INBOX_SORT_BY.NEWEST;
  displayOptions.value.forEach(option => {
    option.selected =
      option.type === INBOX_FILTER_TYPE.STATUS
        ? option.key === status
        : option.key === type;
    activeDisplayFilter[option.type] = option.selected ? option.key : '';
  });
};

onMounted(setSavedFilter);
</script>

<template>
  <div
    class="z-50 flex w-fit min-w-[170px] max-w-64 flex-col divide-y divide-solid divide-polaris-border rounded-polaris-md border border-solid border-polaris-border bg-polaris-popover text-polaris-popover-foreground shadow-md"
  >
    <div class="flex h-11 items-center justify-between gap-2 p-3">
      <div class="flex min-w-0 items-center gap-1.5">
        <Icon icon="i-lucide-arrow-down-up" class="size-3.5 shrink-0" />
        <span class="min-w-0 truncate type-polaris-small-label">
          {{ $t('INBOX.DISPLAY_MENU.SORT') }}
        </span>
      </div>
      <div v-on-clickaway="() => (showSortMenu = false)" class="relative">
        <NextButton
          :label="activeSortOption"
          icon="i-lucide-chevron-down"
          slate
          trailing-icon
          xs
          outline
          class="w-fit min-w-20 max-w-32"
          @click="openSortMenu"
        />
        <div
          v-if="showSortMenu"
          class="absolute top-px z-[60] flex w-fit min-w-20 max-w-32 flex-col gap-0.5 rounded-polaris-md border border-solid border-polaris-border bg-polaris-popover p-0.5 shadow-md"
        >
          <div
            v-for="option in sortOptions"
            :key="option.key"
            role="button"
            class="flex h-6 w-full items-center justify-between gap-2 whitespace-nowrap rounded-polaris-sm px-1.5 hover:bg-polaris-accent hover:text-polaris-accent-foreground"
            :class="{ 'bg-polaris-selected': activeSort === option.key }"
            @click.stop="onSortOptionClick(option)"
          >
            <span class="min-w-0 truncate type-polaris-small-label">
              {{ option.name }}
            </span>
            <Icon
              v-if="activeSort === option.key"
              icon="i-lucide-check"
              class="size-3 shrink-0"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="pb-1">
      <span
        class="block px-3 pb-1 pt-3 type-polaris-caption text-polaris-muted-foreground"
      >
        {{ $t('INBOX.DISPLAY_MENU.DISPLAY') }}
      </span>
      <div class="flex flex-col">
        <label
          v-for="option in displayOptions"
          :key="option.key"
          class="m-0 flex h-9 cursor-pointer items-center gap-2 px-3"
        >
          <Checkbox
            :model-value="option.selected"
            @update:model-value="updateDisplayOption(option)"
          />
          <span class="type-polaris-small-label">{{ option.name }}</span>
        </label>
      </div>
    </div>
  </div>
</template>
